import React, { useState, useRef, useEffect } from 'react';
import { Package, MapPin, Tags, BarChart3, Settings, Archive, Truck, Edit3, X, ArrowRightLeft, Target, FolderTree, ChevronLeft, ChevronRight, Ruler, User, Box, Layers, Wand2 } from 'lucide-react';
import { MaintenanceDataTable } from './components/MaintenanceDataTable';
import { MaintenanceSidePanel } from './components/MaintenanceSidePanel';
import { MovementPriorityDataTable } from './components/MovementPriorityDataTable';
import { MovementPrioritySidePanel } from './components/MovementPrioritySidePanel';
import { MovementTypeDataTable } from './components/MovementTypeDataTable';
import { MovementTypeSidePanel } from './components/MovementTypeSidePanel';
import { PriorityDataTable } from './components/PriorityDataTable';
import { PrioritySidePanel } from './components/PrioritySidePanel';
import { BinCategoryDataTable } from './components/BinCategoryDataTable';
import { BinCategorySidePanel } from './components/BinCategorySidePanel';
import { BinTypeDataTable } from './components/BinTypeDataTable';
import { BinTypeSidePanel } from './components/BinTypeSidePanel';
import { BinGrpDataTable } from './components/BinGrpDataTable';
import { BinGrpSidePanel } from './components/BinGrpSidePanel';
import { BinSizeDataTable } from './components/BinSizeDataTable';
import { BinSizeSidePanel } from './components/BinSizeSidePanel';
import { ResourceDataTable } from './components/ResourceDataTable';
import { ResourceSidePanel } from './components/ResourceSidePanel';
import { SortingBoxDefDataTable } from './components/SortingBoxDefDataTable';
import { SortingBoxDefSidePanel } from './components/SortingBoxDefSidePanel';
import { ZoneDataTable } from './components/ZoneDataTable';
import { ZoneSidePanel } from './components/ZoneSidePanel';
import { BinGrpRelationDataTable } from './components/BinGrpRelationDataTable';
import { BinGrpRelationSidePanel } from './components/BinGrpRelationSidePanel';
import { ItemBinSizeDataTable } from './components/ItemBinSizeDataTable';
import { ItemBinSizeSidePanel } from './components/ItemBinSizeSidePanel';
import { MasterDataWizard } from './components/MasterDataWizard';
import { LocationsDataTable } from './components/locations';
import { ItemsDataTable, ItemsDataTable_Legacy } from './components/items';
import { StcMovementPriorityDto } from '../../../../services/stcMovementPriorityService';
import { StcMovementTypeDto } from '../../../../services/stcMovementTypeService';
import { StcPriorityDto } from '../../../../services/stcPriorityService';
import { StcBinCategoryDto } from '../../../../services/stcBinCategoryService';
import { StcBinTypeDto } from '../../../../services/stcBinTypeService';
import { StcBinGrpDto } from '../../../../services/stcBinGrpService';
import { StcBinSizeDto } from '../../../../types/stcBinSize';
import { StcResourceDto } from '../../../../services/stcResourceService';
import { StcSortingBoxDefDto } from '../../../../services/stcSortingBoxDefService';
import { StcZoneDto } from '../../../../services/stcZoneService';
import { StcBinGrpRelationDto } from '../../../../services/stcBinGrpRelationService';
import { ItemBinSizeDto } from '../../../../types/itemBinSize';

interface MaintenanceModule {
    id: string;
    name: string;
    icon: React.ComponentType<any>;
    description: string;
    tableName: string;
    fields: string[];
}

interface SelectedRows {
    [key: string]: boolean;
}

interface WarehouseMaintenanceProps {
  onBack?: () => void;
}

export const WarehouseMaintenance: React.FC<WarehouseMaintenanceProps> = ({ onBack }) => {
    const [activeModule, setActiveModule] = useState<string>('');
    const [showWizard, setShowWizard] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<SelectedRows>({});
    const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
    const [movementPriorityData, setMovementPriorityData] = useState<StcMovementPriorityDto[]>([]);
    const [movementTypeData, setMovementTypeData] = useState<StcMovementTypeDto[]>([]);
    const [priorityData, setPriorityData] = useState<StcPriorityDto[]>([]);
    const [binCategoryData, setBinCategoryData] = useState<StcBinCategoryDto[]>([]);
    const [binTypeData, setBinTypeData] = useState<StcBinTypeDto[]>([]);
    const [binGrpData, setBinGrpData] = useState<StcBinGrpDto[]>([]);
    const [binSizeData, setBinSizeData] = useState<StcBinSizeDto[]>([]);
    const [resourceData, setResourceData] = useState<StcResourceDto[]>([]);
    const [sortingBoxDefData, setSortingBoxDefData] = useState<StcSortingBoxDefDto[]>([]);
    const [zoneData, setZoneData] = useState<StcZoneDto[]>([]);
    const [binGrpRelationData, setBinGrpRelationData] = useState<StcBinGrpRelationDto[]>([]);
    const [itemBinSizeData, setItemBinSizeData] = useState<ItemBinSizeDto[]>([]);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
    
    // Refs for scrolling functionality
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const activeTabRef = useRef<HTMLButtonElement>(null);

    const maintenanceModules: MaintenanceModule[] = [
        { 
            id: 'items', 
            name: 'Varer', 
            icon: Package, 
            description: 'Vedlikehold varedata',
            tableName: 'Items',
            fields: ['ItemNo', 'Description1', 'UnitOfMeasure', 'ItemGroup']
        },
        { 
            id: 'locations', 
            name: 'Lokasjoner', 
            icon: MapPin, 
            description: 'Vedlikehold lagerlokasjoner',
            tableName: 'StcBin',
            fields: ['BinCode', 'Description1', 'WarehouseID', 'StcZoneID']
        },
        { 
            id: 'resources', 
            name: 'Ressurser', 
            icon: User, 
            description: 'Vedlikehold ressurser',
            tableName: 'StcResource',
            fields: ['ResourceNo', 'Description1']
        },
        { 
            id: 'zones', 
            name: 'Lagersoner', 
            icon: Layers, 
            description: 'Vedlikehold lagersoner',
            tableName: 'StcZone',
            fields: ['StcZoneID', 'WarehouseID', 'Description1']
        },
        { 
            id: 'location-categories', 
            name: 'Lagerkategorier', 
            icon: FolderTree, 
            description: 'Vedlikehold lagerkategorier',
            tableName: 'StcBinCategory',
            fields: ['StcBinCatNo', 'lng_1', 'lng_2', 'lng_3']
        },
        { 
            id: 'location-types', 
            name: 'Lokasjonstyper', 
            icon: Tags, 
            description: 'Vedlikehold lokasjonstyper',
            tableName: 'StcBinType',
            fields: ['BinTypeNo', 'Description1', 'PickPriorityNo', 'PutAwayPriorityNo']
        },
        { 
            id: 'location-groups', 
            name: 'Lokasjonsgrupper', 
            icon: Archive, 
            description: 'Vedlikehold lokasjonsgrupper',
            tableName: 'StcBinGrp',
            fields: ['BinGrpNo', 'Description1', 'CommonAvailable', 'ExternalSystemID']
        },
        { 
            id: 'location-group-relations', 
            name: 'Lokasjonsgrupperelasjon', 
            icon: ArrowRightLeft, 
            description: 'Vedlikehold lokasjonsgrupperelasjon',
            tableName: 'StcBinGrpRelation',
            fields: ['MasterBinGrpNo', 'ChildBinGrpNo']
        },
        { 
            id: 'bin-sizes', 
            name: 'Lokasjonsstørrelser', 
            icon: Ruler, 
            description: 'Vedlikehold lokasjonsstørrelser',
            tableName: 'StcBinSize',
            fields: ['BinSizeNo', 'Description1', 'Width', 'Depth', 'Height', 'TotalVolume', 'UnlimitedYesNo']
        },
        { 
            id: 'sorting-box-defs', 
            name: 'Sorteringsbokser', 
            icon: Box, 
            description: 'Vedlikehold sorteringsboks definisjoner',
            tableName: 'StcSortingBoxDef',
            fields: ['StcBoxTypeID', 'Description1', 'Width', 'Depth', 'Height', 'TotalVolume']
        },
        { 
            id: 'movement-priorities', 
            name: 'Flytteprioriteringer', 
            icon: Truck, 
            description: 'Vedlikehold flytteprioriteringer',
            tableName: 'StcMovementPriority',
            fields: ['MovementTypeNo', 'BinTypeNo', 'FromPriority', 'ToPriority']
        },
        { 
            id: 'movement-types', 
            name: 'Bevegelseskoder', 
            icon: ArrowRightLeft, 
            description: 'Vedlikehold bevegelseskoder',
            tableName: 'StcMovementType',
            fields: ['MovementTypeNo', 'Description1']
        },
        { 
            id: 'priorities', 
            name: 'Plasseringsprioritet', 
            icon: Target, 
            description: 'Vedlikehold plasseringsprioriteringer',
            tableName: 'StcPriority',
            fields: ['PriorityNo', 'Description1']
        },
        { 
            id: 'barcode-elements', 
            name: 'Strekkodeelementer', 
            icon: BarChart3, 
            description: 'Vedlikehold strekkodeelementer',
            tableName: 'BarcodeElement',
            fields: ['BCElementID', 'Description', 'AI', 'Length']
        },
        {
            id: 'barcode-lines',
            name: 'Strekkodelinjer',
            icon: Settings,
            description: 'Vedlikehold strekkodelinjer',
            tableName: 'BarcodeLine',
            fields: ['BarcodeID', 'BCElementID', 'Description', 'ActiveYesNo']
        },
        {
            id: 'item-bin-sizes',
            name: 'Vare-lokasjonsstørrelser',
            icon: Box,
            description: 'Vedlikehold vare-lokasjonsstørrelser',
            tableName: 'ItemBinSize',
            fields: ['ItemNo', 'ItemDescription', 'BinSizeDescription', 'PutAwayPriorityNo', 'MaxQuantity']
        }
    ];

    // Auto-scroll to active tab when activeModule changes
    useEffect(() => {
        if (activeModule && activeTabRef.current && scrollContainerRef.current) {
            const activeTab = activeTabRef.current;
            const container = scrollContainerRef.current;
            
            // Get positions
            const containerRect = container.getBoundingClientRect();
            const tabRect = activeTab.getBoundingClientRect();
            
            // Calculate if tab is visible and where to scroll
            const containerLeft = containerRect.left;
            const containerRight = containerRect.right;
            const tabLeft = tabRect.left;
            const tabRight = tabRect.right;
            
            // If tab is not fully visible, scroll to center it
            if (tabLeft < containerLeft || tabRight > containerRight) {
                const tabCenterX = tabRect.left + tabRect.width / 2;
                const containerCenterX = containerRect.left + containerRect.width / 2;
                const scrollOffset = tabCenterX - containerCenterX;
                
                container.scrollBy({
                    left: scrollOffset,
                    behavior: 'smooth'
                });
            }
        }
    }, [activeModule]);

    const handleModuleClick = (moduleId: string) => {
        setActiveModule(moduleId);
        setSelectedRows({});
        setSidePanelOpen(false);
    };

    const handleRowSelection = (rowId: string, selected: boolean) => {
        setSelectedRows(prev => ({
            ...prev,
            [rowId]: selected
        }));
    };

    const handleBulkSelection = (rows: SelectedRows) => {
        setSelectedRows(rows);
    };

    const getSelectedRowCount = (): number => {
        return Object.values(selectedRows).filter(selected => selected).length;
    };

    const handleOpenSidePanel = () => {
        if (getSelectedRowCount() > 0) {
            setSidePanelOpen(true);
        }
    };

    const handleCloseSidePanel = () => {
        setSidePanelOpen(false);
    };

    const handleScrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: -200,
                behavior: 'smooth'
            });
        }
    };

    const handleScrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: 200,
                behavior: 'smooth'
            });
        }
    };

    const activeModuleData = maintenanceModules.find(module => module.id === activeModule);

    return (
        <div className="w-full">
            {/* Custom CSS to hide scrollbar */}
            <style>{`
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
            `}</style>

            <div className="px-6 pt-6 space-y-6">
            {showWizard ? (
                <MasterDataWizard onClose={() => setShowWizard(false)} />
            ) : !activeModule ? (
                /* Module Selection */
                <div className="space-y-6">
                    {/* Wizard Call-to-Action */}
                    <div 
                        onClick={() => setShowWizard(true)}
                        className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/20 p-4 rounded-lg group-hover:scale-110 transition-transform">
                                    <Wand2 className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-white mb-1">Grunndata Wizard</h3>
                                    <p className="text-white/90 text-sm">Bli veiledet gjennom oppsett av alle grunndata-modulene i riktig rekkefølge</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-white">
                                <span className="text-sm font-medium">Start veiledet oppsett</span>
                                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </div>
                    </div>

                    {/* Module Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {maintenanceModules.map(module => {
                        const Icon = module.icon;
                        return (
                            <div 
                                key={module.id}
                                className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
                                onClick={() => handleModuleClick(module.id)}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                                        <Icon className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-gray-900 mb-1 text-sm">{module.name}</h3>
                                        <p className="text-xs text-gray-600 line-clamp-2">{module.description}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    </div>
                </div>
            ) : (
                /* Active Module View */
                <div className="space-y-4">
                    {/* Top Navigation */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between p-3 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => setActiveModule('')}
                                    className="text-gray-500 hover:text-gray-700 transition-colors p-1"
                                    title="Tilbake til moduler"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                                <h2 className="text-lg font-semibold text-gray-900">
                                    {activeModuleData?.name}
                                </h2>
                            </div>
                        </div>

                        {/* Scrollable Module Tabs */}
                        <div className="relative">
                            <div className="flex items-center">
                                {/* Left Scroll Button */}
                                <button
                                    onClick={handleScrollLeft}
                                    className="hidden sm:flex items-center justify-center w-10 h-12 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 border-r border-gray-100"
                                    title="Scroll til venstre"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                {/* Scrollable Container */}
                                <div 
                                    ref={scrollContainerRef}
                                    className="flex-1 overflow-x-auto scrollbar-hide"
                                >
                                    <div className="flex min-w-max">
                                        {maintenanceModules.map(module => {
                                            const Icon = module.icon;
                                            const isActive = module.id === activeModule;
                                            return (
                                                <button
                                                    key={module.id}
                                                    ref={isActive ? activeTabRef : null}
                                                    onClick={() => handleModuleClick(module.id)}
                                                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                                                        isActive 
                                                            ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600 relative' 
                                                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <Icon className="w-4 h-4" />
                                                    <span className="hidden sm:inline">{module.name}</span>
                                                    {isActive && (
                                                        <div className="absolute inset-0 bg-blue-600 opacity-5 rounded-t-sm"></div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Right Scroll Button */}
                                <button
                                    onClick={handleScrollRight}
                                    className="hidden sm:flex items-center justify-center w-10 h-12 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 border-l border-gray-100"
                                    title="Scroll til høyre"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Data Table */}
                    {activeModuleData && (
                        activeModule === 'items' ? (
                            window.location.hash === '#show-legacy-items' ? (
                                <ItemsDataTable_Legacy />
                            ) : (
                                <ItemsDataTable />
                            )
                        ) : activeModule === 'locations' ? (
                            <LocationsDataTable />
                        ) : activeModule === 'zones' ? (
                            <ZoneDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setZoneData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'movement-priorities' ? (
                            <MovementPriorityDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setMovementPriorityData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'movement-types' ? (
                            <MovementTypeDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setMovementTypeData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'priorities' ? (
                            <PriorityDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setPriorityData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'location-categories' ? (
                            <BinCategoryDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setBinCategoryData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'location-types' ? (
                            <BinTypeDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setBinTypeData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'location-groups' ? (
                            <BinGrpDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setBinGrpData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'location-group-relations' ? (
                            <BinGrpRelationDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setBinGrpRelationData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'bin-sizes' ? (
                            <BinSizeDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setBinSizeData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'resources' ? (
                            <ResourceDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setResourceData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'sorting-box-defs' ? (
                            <SortingBoxDefDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setSortingBoxDefData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : activeModule === 'item-bin-sizes' ? (
                            <ItemBinSizeDataTable
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                                onDataChange={setItemBinSizeData}
                                onOpenSidePanel={handleOpenSidePanel}
                                refreshTrigger={refreshTrigger}
                            />
                        ) : (
                            <MaintenanceDataTable
                                module={activeModuleData}
                                selectedRows={selectedRows}
                                onRowSelection={handleRowSelection}
                                onBulkSelection={handleBulkSelection}
                            />
                        )
                    )}
                </div>
            )}

            {/* Side Panel for Editing */}
            {sidePanelOpen && activeModuleData && (
                activeModule === 'zones' ? (
                    <ZoneSidePanel
                        selectedRows={selectedRows}
                        data={zoneData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'movement-priorities' ? (
                    <MovementPrioritySidePanel
                        selectedRows={selectedRows}
                        data={movementPriorityData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'movement-types' ? (
                    <MovementTypeSidePanel
                        selectedRows={selectedRows}
                        data={movementTypeData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'priorities' ? (
                    <PrioritySidePanel
                        selectedRows={selectedRows}
                        data={priorityData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'location-categories' ? (
                    <BinCategorySidePanel
                        selectedRows={selectedRows}
                        data={binCategoryData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'location-types' ? (
                    <BinTypeSidePanel
                        selectedRows={selectedRows}
                        data={binTypeData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'location-groups' ? (
                    <BinGrpSidePanel
                        selectedRows={selectedRows}
                        data={binGrpData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'location-group-relations' ? (
                    <BinGrpRelationSidePanel
                        selectedRows={selectedRows}
                        data={binGrpRelationData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'bin-sizes' ? (
                    <BinSizeSidePanel
                        selectedRows={selectedRows}
                        data={binSizeData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'resources' ? (
                    <ResourceSidePanel
                        selectedRows={selectedRows}
                        data={resourceData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'sorting-box-defs' ? (
                    <SortingBoxDefSidePanel
                        selectedRows={selectedRows}
                        data={sortingBoxDefData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : activeModule === 'item-bin-sizes' ? (
                    <ItemBinSizeSidePanel
                        selectedRows={selectedRows}
                        data={itemBinSizeData}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            setRefreshTrigger(prev => prev + 1);
                            setSidePanelOpen(false);
                            setSelectedRows({});
                        }}
                    />
                ) : (
                    <MaintenanceSidePanel
                        module={activeModuleData}
                        selectedRows={selectedRows}
                        onClose={handleCloseSidePanel}
                        onSave={() => {
                            console.log('Save changes for selected rows:', selectedRows);
                            setSidePanelOpen(false);
                        }}
                    />
                )
            )}
            </div>
        </div>
    );
};
